import { defineStore } from "pinia";
import { ref, computed } from "vue";
import employeeLeaveService from "@/services/hrm/employeeLeaveService";
import { toast } from "@/utils/toast";
import { MODULE_KEYS } from "@/config/modules-registry";
import { guardPlanModule } from "@/utils/planAccess";

export const useEmployeeLeaveStore = defineStore("employeeLeaves", () => {
  const items = ref([]);
  const loading = ref(false);
  const error = ref(null);
  const pagination = ref({ total: 0, per_page: 25, current_page: 1 });

  // Summary for cards
  const summary = ref({
    totals: { total_taken: 0, total_remaining: 0 },
    types: [] // [{leave_type_id, name, code, default_days, taken, remaining}]
  });

  const filters = ref({
    q: "",
    date_from: "",
    date_to: "",
    status: "",   // Approved | Declined | New
    type_id: "",
    per_page: 25,
    page: 1,
    sort: "recent",
  });

  const totalPages = computed(() => {
    const t = pagination.value.total || 0;
    const p = pagination.value.per_page || 25;
    return Math.max(1, Math.ceil(t / p));
  });

  async function fetchSummary(extra = {}) {
    loading.value = true;
    error.value = null;
    try {
      const params = { year: new Date().getFullYear(), ...extra };
      const { data } = await employeeLeaveService.getMySummary(params);
      summary.value = data || { totals: {}, types: [] };
    } catch (e) {
      error.value = e?.response?.data?.message || "Failed to load summary";
      toast(error.value, "error");
    } finally {
      loading.value = false;
    }
  }

  async function fetchAll(extra = {}) {
    if (!(await guardPlanModule(MODULE_KEYS.HRM_ATTENDANCE))) {
      items.value = [];
      pagination.value = { total: 0, per_page: 25, current_page: 1 };
      return;
    }

    loading.value = true;
    error.value = null;
    try {
      const params = { ...filters.value, ...extra };
      const res = await employeeLeaveService.getMyLeaves(params);
      const data = res.data;

      if (data?.data && (data?.meta || data?.total !== undefined)) {
        items.value = data.data;
        pagination.value = {
          total: data.meta?.total ?? data.total ?? data.data.length,
          per_page: data.meta?.per_page ?? data.per_page ?? params.per_page ?? 25,
          current_page: data.meta?.current_page ?? data.current_page ?? 1,
        };
      } else {
        items.value = Array.isArray(data) ? data : [];
        pagination.value = {
          total: items.value.length,
          per_page: params.per_page ?? 25,
          current_page: 1,
        };
      }
    } catch (e) {
      error.value = e?.response?.data?.message || "Failed to fetch leaves";
      toast(error.value, "error");
    } finally {
      loading.value = false;
    }
  }

  async function create(payload) {
    loading.value = true;
    try {
      await employeeLeaveService.create(payload);
      toast("Leave request submitted");
      await Promise.all([fetchAll({ page: 1 }), fetchSummary()]);
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to submit leave";
      toast(msg, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function update(id, payload) {
    loading.value = true;
    try {
      await employeeLeaveService.update(id, payload);
      toast("Leave updated");
      await Promise.all([fetchAll(), fetchSummary()]);
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to update leave";
      toast(msg, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function remove(id) {
    loading.value = true;
    try {
      await employeeLeaveService.delete(id);
      toast("Leave deleted");
      await Promise.all([fetchAll(), fetchSummary()]);
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to delete leave";
      toast(msg, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  return {
    items, loading, error, pagination, filters, summary, totalPages,
    fetchAll, fetchSummary, create, update, remove,
  };
});
